import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDate, isValidDate, sortServices } from "../../services/helpers";
import { getVendors } from "../../services/vendorService";
import { Vendor } from "../../interfaces/vendor";
import { Service } from "../../interfaces/service";
import { getAllServices } from "../../services/serviceService";
import ToolTip from "../../components/component-tools/ToolTip";
import ConfirmDelete from "../../components/component-tools/ConfirmDelete";
import { useViewOrderContext } from "../../contexts/ViewOrderContext";
import { getSimpleOrderByID } from "../../services/orderService";
import { SimpleOrder } from "../../interfaces/simpleOrder";
import ConfirmPopup from "../../components/client-components/ConfirmPopup";
import { SubscriptionDetail } from "../../interfaces/subscriptionDetail";
import { SubscriptionOrder } from "../../interfaces/subscriptionOrder";
import {
  createSubscriptionDetail,
  deleteSubscriptionDetail,
  getSubscriptionDetailByID,
  stopSubscriptionDetail,
  updateSubscriptionDetail,
} from "../../services/subscriptionDetailService";
import { getSubscriptionOrderByID } from "../../services/subscriptionOrderService";
import ordinal from "ordinal";
import { SubscriptionInvoice } from "../../interfaces/subscriptionInvoice";
import { getSubscriptionInvoicesByDetail } from "../../services/subscriptionInvoiceService";

const SubscriptionDetailCEV = () => {
  const [subscriptionDetail, setSubscriptionDetail] =
    useState<SubscriptionDetail | null>(null);
  const [subscriptionOrder, setSubscriptionOrder] =
    useState<SubscriptionOrder | null>(null);
  const [order, setOrder] = useState<SimpleOrder | null>(null);
  const { state } = useLocation();
  const subscriptionOrderID = state?.subscriptionOrderID || null;
  const subscriptionDetailID = state?.subscriptionDetailID || null;
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>();
  const [services, setServices] = useState<Service[]>();
  const [isFeeManuallySet, setIsFeeManuallySet] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { setSubscriptionDetails, subscriptionDetails } = useViewOrderContext();
  const [editing, setEditing] = useState(true);
  const [
    showConfirmationStopSubscription,
    setShowConfirmationStopSubscription,
  ] = useState(false);
  const [subscriptionInvoices, setSubscriptionInvoices] =
    useState<SubscriptionInvoice[]>();

  const statusClassMap: Record<number, string> = {
    1: "status-pending",
    2: "status-admin-review",
    3: "status-in-process",
    4: "status-completed",
    5: "status-deleted",
    6: "status-subscription",
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchSubscriptionOrderDetails = async () => {
      try {
        if (subscriptionDetailID) {
          const id = parseInt(subscriptionDetailID, 10);
          const response = await getSubscriptionDetailByID(id);
          setSubscriptionDetail(response);

          const invoices = await getSubscriptionInvoicesByDetail(
            response.subscriptionDetailID,
          );
          setSubscriptionInvoices(invoices);
        } else {
          setSubscriptionDetail({
            subscriptionDetailID: 0,
            subscriptionOrderID: subscriptionOrderID,
            serviceID: 36,
            vendorID: 4,
            initiatedDate: undefined,
            stopDate: undefined,
            resultsFieldOne: "",
            resultsFieldTwo: "",
            fee: 0,
            redFlag: false,
            comments: "",
            adminReviewed: false,
          });
        }
      } catch (err) {
        console.log("Failed to fetch subscription detail.");
      } finally {
        setLoadingSpinner(false);
      }

      // get subscription order and order
      try {
        const subscriptionOrderResponse =
          await getSubscriptionOrderByID(subscriptionOrderID);
        setSubscriptionOrder(subscriptionOrderResponse);

        const orderResponse = await getSimpleOrderByID(
          subscriptionOrderResponse.orderID,
        );
        setOrder(orderResponse);
      } catch {
        console.log("Subscription Order ID not found.");
      }

      // get all vendors
      const vendorResponse = await getVendors();
      const sortedVendors = vendorResponse.filter(
        (vendor) => vendor.vendorID === 4,
      );
      setVendors(sortedVendors);

      // get all services
      const serviceResponse = await getAllServices();
      const filteredServices = serviceResponse.filter(
        (service) => service.subscriptionService,
      );
      const sortedServices = await sortServices(filteredServices);
      setServices(sortedServices);
    };

    fetchSubscriptionOrderDetails();
  }, [subscriptionDetailID, subscriptionOrderID]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (
      subscriptionDetail?.serviceID &&
      services &&
      !isFeeManuallySet &&
      !subscriptionDetailID
    ) {
      const selectedService = services.find(
        (service) => service.serviceID === Number(subscriptionDetail.serviceID),
      );

      if (selectedService) {
        setSubscriptionDetail((prev) => ({
          ...prev!,
          fee: selectedService.fee,
          vendorID: selectedService.vendorID,
        }));
      }
    }
  }, [
    subscriptionDetail?.serviceID,
    services,
    subscriptionDetailID,
    isFeeManuallySet,
  ]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: keyof SubscriptionDetail,
  ) => {
    setEditing(true);
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (field === "fee") {
      setIsFeeManuallySet(true);
    }

    if (subscriptionDetail) {
      setSubscriptionDetail({
        ...subscriptionDetail,
        [field]: value,
      });
    }
  };

  const handleServiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setIsFeeManuallySet(false);
    handleInputChange(e, "serviceID");
  };

  const handleSave = async () => {
    setLoadingSpinner(true);

    try {
      if (
        subscriptionDetail &&
        subscriptionDetail.subscriptionDetailID === 0 &&
        subscriptionOrder
      ) {
        // new
        const result = await createSubscriptionDetail(subscriptionDetail);
        setSubscriptionDetail(result);

        setSubscriptionDetails([...subscriptionDetails, result]);

        navigate(`/admin/view-order/${subscriptionOrder.orderID}`, {
          state: {
            successMessage: "Subscription Detail created successfully.",
          },
        });
      } else if (
        subscriptionDetail &&
        subscriptionDetail.subscriptionDetailID !== 0
      ) {
        // update

        // set received date correctly after clear
        const payload: any = { ...subscriptionDetail };
        if (payload.receivedDate === "") {
          payload.receivedDate = null;
        }

        const result = await updateSubscriptionDetail(
          payload as SubscriptionDetail,
        );
        setSubscriptionDetail(result);

        setSubscriptionDetails((prev) =>
          prev.map((cd) =>
            cd.subscriptionDetailID === result.subscriptionDetailID
              ? result
              : cd,
          ),
        );
        setSuccessMessage("Updated Successfully.");
        setEditing(false);
      }
    } catch (error) {
      console.error("Error saving subscription details:", error);
      alert("An error occurred while saving the form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleStopSubscription = async () => {
    if (!subscriptionDetail) return;

    setLoadingSpinner(true);

    try {
      const result = await stopSubscriptionDetail(
        subscriptionDetail.subscriptionDetailID,
      );

      setSubscriptionDetail(result);
      setSubscriptionDetails((prev) =>
        prev.map((cd) =>
          cd.subscriptionDetailID === result.subscriptionDetailID ? result : cd,
        ),
      );

      setSuccessMessage("Updated Successfully.");
      setEditing(false);
      setShowConfirmationStopSubscription(false);
    } catch (error) {
      console.error("Error stopping subscription:", error);
      alert("An error occurred while saving the form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (subscriptionDetailID && subscriptionDetail && subscriptionOrder) {
        await deleteSubscriptionDetail(
          subscriptionDetail?.subscriptionDetailID,
        );

        setSubscriptionDetails(
          subscriptionDetails.filter(
            (cd) =>
              cd.subscriptionDetailID !==
              subscriptionDetail.subscriptionDetailID,
          ),
        );

        navigate(`/admin/view-order/${subscriptionOrder.orderID}`, {
          state: {
            successMessage: "Subscription Detail deleted successfully.",
          },
        });
      }
    } catch (error) {
      console.error("Error deleting subscription detail:", error);
      setSuccessMessage("");
    }
  };

  return (
    <div className="content-container">
      <div className="data-container">
        <div
          className="page-items-container"
          style={{
            alignItems: "center",
            flex: "0 0 100%",
            justifyContent: "space-between",
          }}
        >
          <h1>
            {subscriptionDetailID
              ? "View Subscription Service"
              : "Create Subscription Service"}
          </h1>
          {order !== null && (
            <div
              className="page-items-container"
              style={{ alignItems: "center" }}
            >
              <h3>
                {order?.candidateFirstName + " " + order?.candidateLastName}
              </h3>
              <div
                className={`chip-date ${
                  statusClassMap[order?.orderStatusID ?? 1] || "status-unknown"
                }`}
                style={{
                  gap: "10px",
                  justifyContent: "end",
                }}
              >
                <div>
                  {order.orderStatusID === 2
                    ? "Admin Review"
                    : order.orderStatusID === 3
                      ? "In Process"
                      : order.orderStatusID === 4
                        ? "Completed"
                        : order.orderStatusID === 5
                          ? "Deleted"
                          : order.orderStatusID === 6
                            ? "Subscription"
                            : "Pending"}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="divider-small" style={{ margin: "0px" }}></div>
        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Service:</label>
          <div
            style={{
              flex: "0 0 30%",
              alignSelf: "end",
              display: "flex",
              flexDirection: "row",
            }}
          >
            {services && (
              <select
                className={`form-select`}
                value={subscriptionDetail?.serviceID || ""}
                onChange={(e) => handleServiceChange(e)}
                disabled={isValidDate(subscriptionDetail?.initiatedDate)}
              >
                {services.map((service) => (
                  <option key={service.serviceID} value={service.serviceID}>
                    {service.serviceName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Vendor:</label>
          <div
            style={{
              flex: "0 0 30%",
              alignSelf: "end",
              display: "flex",
              flexDirection: "row",
            }}
          >
            {vendors && (
              <select
                className={`form-select`}
                value={subscriptionDetail?.vendorID || ""}
                onChange={(e) => handleInputChange(e, "vendorID")}
                disabled={isValidDate(subscriptionDetail?.initiatedDate)}
              >
                {vendors.map((vendor) => (
                  <option key={vendor.vendorID} value={vendor.vendorID}>
                    {vendor.vendorName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 10%" }}>
          <label className="field-label">Fee:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={subscriptionDetail?.fee || ""}
              onChange={(e) => handleInputChange(e, "fee")}
            />
          </div>
        </div>
        <div style={{ flex: "0 0 100%" }}></div>
        <div style={{ flex: "0 0 100%" }}>
          <div className="text-sub-sub-header">Invoice Tracking</div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            {subscriptionInvoices?.length !== 0 ? (
              subscriptionInvoices?.map((invoice, index) => (
                <div
                  key={invoice.subscriptionInvoiceID}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <label className="field-label" style={{ paddingTop: "20px" }}>
                    {`${ordinal(index + 1)} Invoice`}:
                  </label>

                  <div className="field-stacked" style={{ flex: "0 0 15%" }}>
                    <div style={{ display: "flex", gap: "5px" }}>
                      <label className="field-label">Invoiced: </label>
                    </div>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="text"
                        value={formatDate(invoice.invoiceDate, "us")}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="field-stacked" style={{ flex: "0 0 15%" }}>
                    <div style={{ display: "flex", gap: "5px" }}>
                      <label className="field-label">Billed: </label>
                    </div>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="text"
                        value={formatDate(invoice.billedDate, "us")}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div>no invoices.</div>
            )}
          </div>
        </div>
        <div style={{ flex: "0 0 100%" }}></div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <div style={{ display: "flex", gap: "5px" }}>
            <label className="field-label">Initiated: </label>
            <ToolTip
              tooltipText="The Initiated Date is system-generated when the order is processed in the vendor table."
              placement="right"
              className="info-icon-spec"
            ></ToolTip>
          </div>

          <div className="field-input">
            <input
              className="form-control"
              type="date"
              value={
                subscriptionDetail?.initiatedDate !== undefined
                  ? formatDate(subscriptionDetail?.initiatedDate)
                  : ""
              }
              onChange={(e) => handleInputChange(e, "initiatedDate")}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <div style={{ display: "flex", gap: "5px" }}>
            <label className="field-label">End: </label>
            <ToolTip
              tooltipText="When this subscription is stopped with the vendor."
              placement="right"
              className="info-icon-spec"
            ></ToolTip>
          </div>
          <div className="field-input">
            <input
              className="form-control"
              type="date"
              value={
                subscriptionDetail?.stopDate !== undefined
                  ? formatDate(subscriptionDetail?.stopDate)
                  : ""
              }
              onChange={(e) => handleInputChange(e, "stopDate")}
              disabled={true}
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: "15px" }}>
          {showConfirmationStopSubscription && (
            <ConfirmPopup
              onConfirm={() => {
                handleStopSubscription();
              }}
              onCancel={() => setShowConfirmationStopSubscription(false)}
              message="Confirming will let the vendor know to stop subscription for this order."
            />
          )}
          {subscriptionDetail?.subscriptionDetailID !== 0 &&
            subscriptionDetail?.stopDate === null &&
            subscriptionDetail.initiatedDate !== null && (
              <div style={{ alignSelf: "end" }}>
                <button
                  className="oxbtn btn-red"
                  onClick={() => setShowConfirmationStopSubscription(true)}
                >
                  Stop Subscription
                </button>
              </div>
            )}
        </div>

        {/* <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Field One:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={subscriptionDetail?.resultsFieldOne || ""}
              onChange={(e) => handleInputChange(e, "resultsFieldOne")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Field Two:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={subscriptionDetail?.resultsFieldTwo || ""}
              onChange={(e) => handleInputChange(e, "resultsFieldTwo")}
            />
          </div>
        </div> */}
        {/* <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Red Flag:</label>
          <div>
            <input
              className="form-check-input check-red"
              type="checkbox"
              checked={
                subscriptionDetail?.redFlag
                  ? subscriptionDetail?.redFlag
                  : false
              }
              onChange={(e) => handleInputChange(e, "redFlag")}
            />
          </div>
        </div> */}

        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Comments:</label>
          <div className="field-input">
            <textarea
              className="form-control"
              value={subscriptionDetail?.comments || ""}
              onChange={(e) => handleInputChange(e, "comments")}
              style={{ height: "100px" }}
              maxLength={2000}
            />
            <div
              style={{
                fontSize: "14px",
                marginTop: "4px",
                textAlign: "right",
              }}
            >
              {subscriptionDetail?.comments?.length ?? 0}/2000
            </div>
          </div>
        </div>

        <div style={{ flex: "0 0 100%" }}>
          {successMessage && (
            <div className=" alert alert-success fade show">
              {successMessage}
            </div>
          )}
        </div>
      </div>

      <div className="action-buttons">
        <button
          className={`oxbtn btn-green ${!editing ? "disabled" : ""}`}
          onClick={() => handleSave()}
          disabled={!editing}
        >
          Save
        </button>
        {subscriptionDetail?.initiatedDate == null && subscriptionDetailID && (
          <button
            className="oxbtn btn-red"
            onClick={() => setShowConfirmation(true)}
          >
            Delete
          </button>
        )}
        {showConfirmation && (
          <ConfirmDelete
            onConfirm={() => handleDelete()}
            onCancel={() => setShowConfirmation(false)}
            message="Are you sure you want to delete this service?"
          />
        )}
      </div>

      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDetailCEV;
